import { Button } from "@/components/ui/button";
import { resolveConflict, selectSheet, uploadFiles } from "@/services/api";
import { useUploadStore } from "@/stores/upload-store";
import { useQueryClient } from "@tanstack/react-query";
import { Upload } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { ConflictDialog } from "./conflict-dialog";
import { SheetSelectDialog } from "./sheet-select-dialog";
import { UploadSubscriber } from "./upload-subscriber";

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

function toNumber(value: unknown): number | null {
	return typeof value === "number" ? value : null;
}

/**
 * Кнопка загрузки файлов — открывает системный диалог выбора файлов.
 * После выбора файлов запускает загрузку и обновляет upload store через polling (useTaskEvents).
 * При получении sheet-select показывает диалог выбора листа.
 */
export function FileUploadButton() {
	const inputRef = useRef<HTMLInputElement>(null);
	const queryClient = useQueryClient();
	const [uploadId, setUploadId] = useState<string | null>(null);
	const isUploading = useUploadStore((s) => s.isUploading);
	const pendingSheetSelect = useUploadStore((s) => s.pendingSheetSelect);
	const pendingConflict = useUploadStore((s) => s.pendingConflict);
	const setSelectedFiles = useUploadStore((s) => s.setSelectedFiles);
	const startUpload = useUploadStore((s) => s.startUpload);
	const updateFileStatus = useUploadStore((s) => s.updateFileStatus);
	const setPendingSheetSelect = useUploadStore((s) => s.setPendingSheetSelect);
	const setPendingConflict = useUploadStore((s) => s.setPendingConflict);
	const addParsingResult = useUploadStore((s) => s.addParsingResult);
	const completeUpload = useUploadStore((s) => s.completeUpload);
	const failUpload = useUploadStore((s) => s.failUpload);
	const setProgress = useUploadStore((s) => s.setProgress);
	const upsertSheetStatus = useUploadStore((s) => s.upsertSheetStatus);

	const handleClick = () => {
		inputRef.current?.click();
	};

	const handleSheetSelect = async (sheetNames: string[]) => {
		if (!pendingSheetSelect) return;
		const { uploadId: serverUploadId, fileIndex } = pendingSheetSelect;
		setPendingSheetSelect(null);
		try {
			await selectSheet(serverUploadId, fileIndex, sheetNames);
		} catch (err) {
			const message = err instanceof Error ? err.message : "Не удалось отправить выбор листа";
			toast.error(message);
			failUpload(message);
			setUploadId(null);
		}
	};

	const handleSheetCancel = async () => {
		if (!pendingSheetSelect) return;
		const { uploadId: serverUploadId, fileIndex } = pendingSheetSelect;
		setPendingSheetSelect(null);
		try {
			await selectSheet(serverUploadId, fileIndex, undefined, true);
		} catch (err) {
			const message = err instanceof Error ? err.message : "Не удалось отменить выбор листа";
			toast.error(message);
			failUpload(message);
			setUploadId(null);
		}
	};

	const handleConflictReplace = async () => {
		if (!pendingConflict) return;
		const { uploadId: serverUploadId, fileIndex } = pendingConflict;
		setPendingConflict(null);
		try {
			await resolveConflict(serverUploadId, fileIndex, "replace");
		} catch (err) {
			const message = err instanceof Error ? err.message : "Не удалось отправить решение";
			toast.error(message);
			failUpload(message);
			setUploadId(null);
		}
	};

	const handleConflictCancel = async () => {
		if (!pendingConflict) return;
		const { uploadId: serverUploadId, fileIndex } = pendingConflict;
		setPendingConflict(null);
		try {
			await resolveConflict(serverUploadId, fileIndex, "cancel");
		} catch (err) {
			const message = err instanceof Error ? err.message : "Не удалось отправить отмену";
			toast.error(message);
			failUpload(message);
			setUploadId(null);
		}
	};

	const handleEvent = useCallback(
		(event: string, data: unknown) => {
			if (!isRecord(data)) return;

			const fileIndex = toNumber(data.fileIndex);

			switch (event) {
				case "progress": {
					if (fileIndex === null) return;
					updateFileStatus(fileIndex, {
						status: "processing",
					});
					break;
				}
				case "file-done": {
					if (fileIndex === null) return;
					updateFileStatus(fileIndex, {
						status: "done",
						fileId: toNumber(data.fileId) ?? undefined,
						fileType: typeof data.fileType === "string" ? data.fileType : undefined,
						objectName:
							typeof data.objectName === "string" || data.objectName === null
								? data.objectName
								: undefined,
						periodMonth:
							typeof data.periodMonth === "number" || data.periodMonth === null
								? data.periodMonth
								: undefined,
						periodYear:
							typeof data.periodYear === "number" || data.periodYear === null
								? data.periodYear
								: undefined,
						sheetName:
							typeof data.sheetName === "string" || data.sheetName === null
								? data.sheetName
								: undefined,
					});
					break;
				}
				case "file-error": {
					if (fileIndex === null) return;
					updateFileStatus(fileIndex, {
						status: "error",
						error: typeof data.reason === "string" ? data.reason : undefined,
					});
					break;
				}
				case "file-skipped": {
					if (fileIndex === null) return;
					updateFileStatus(fileIndex, {
						status: "skipped",
						error: typeof data.reason === "string" ? data.reason : undefined,
					});
					break;
				}
				case "rate-book-parsed":
				case "work-order-parsed": {
					if (fileIndex === null) return;
					const errors = Array.isArray(data.errors)
						? data.errors.flatMap((err) =>
								isRecord(err) && typeof err.row === "number" && typeof err.reason === "string"
									? [{ row: err.row, reason: err.reason }]
									: [],
							)
						: [];

					const fileType = event === "rate-book-parsed" ? "rate_book" : "work_order";

					addParsingResult({
						fileIndex,
						fileType,
						totalRows: toNumber(data.totalRows) ?? 0,
						parsedCount: toNumber(data.parsedCount) ?? 0,
						skippedCount: toNumber(data.skippedCount) ?? 0,
						errors,
					});
					break;
				}
				case "completion-act-parsed": {
					// v3 payload: иерархическая структура + флаги валидации (FR-031).
					if (fileIndex === null) return;
					const issuesPreview = Array.isArray(data.issuesPreview)
						? data.issuesPreview.flatMap((issue) => {
								if (!isRecord(issue)) return [];
								const kind = typeof issue.kind === "string" ? issue.kind : null;
								const scope = typeof issue.scope === "string" ? issue.scope : null;
								const message = typeof issue.message === "string" ? issue.message : null;
								if (!kind || !scope || !message) return [];
								const continuationRows = Array.isArray(issue.continuationRows)
									? issue.continuationRows.filter(
											(n): n is number => typeof n === "number" && Number.isFinite(n),
										)
									: undefined;
								return [
									{
										kind,
										scope: scope as "work" | "material" | "block" | "grand",
										row: toNumber(issue.row) ?? undefined,
										continuationRows:
											continuationRows && continuationRows.length > 0
												? continuationRows
												: undefined,
										expected: toNumber(issue.expected) ?? undefined,
										got: toNumber(issue.got) ?? undefined,
										delta: toNumber(issue.delta) ?? undefined,
										message,
									},
								];
							})
						: [];

					const layoutVariant =
						data.layoutVariant === "with_code_in_a" || data.layoutVariant === "without_code"
							? data.layoutVariant
							: "standard";
					const fileStatus =
						data.fileStatus === "parsed_with_warnings" ? "parsed_with_warnings" : "parsed";

					addParsingResult({
						fileIndex,
						fileType: "completion_act",
						// v3 не использует totalRows/parsedCount/skippedCount — оставляем 0 для обратной совместимости с ParsingResult.
						totalRows: 0,
						parsedCount: toNumber(data.worksCount) ?? 0,
						skippedCount: 0,
						errors: [],
						completionActValidation: {
							arithmeticOk: data.arithmeticOk === true,
							subtotalsOk: data.subtotalsOk === true,
							grandTotalOk: data.grandTotalOk === true,
							blocksCount: toNumber(data.blocksCount) ?? 0,
							worksCount: toNumber(data.worksCount) ?? 0,
							materialsCount: toNumber(data.materialsCount) ?? 0,
							extraLaborCount: toNumber(data.extraLaborCount) ?? 0,
							issuesCount: toNumber(data.issuesCount) ?? 0,
							warningsCount: toNumber(data.warningsCount) ?? 0,
							layoutVariant,
							fileStatus,
							issuesPreview,
						},
					});
					break;
				}
				case "completion-act-parse-failed": {
					if (fileIndex === null) return;
					updateFileStatus(fileIndex, {
						status: "error",
						error:
							typeof data.errorMessage === "string" ? data.errorMessage : "Ошибка парсинга акта",
					});
					break;
				}
				case "sheet-select": {
					if (
						fileIndex === null ||
						typeof data.uploadId !== "string" ||
						typeof data.filename !== "string" ||
						!Array.isArray(data.sheets)
					) {
						return;
					}

					const alreadyLoaded = Array.isArray(data.alreadyLoaded)
						? data.alreadyLoaded.filter((s): s is string => typeof s === "string")
						: undefined;

					setPendingSheetSelect({
						uploadId: data.uploadId,
						fileIndex,
						filename: data.filename,
						sheets: data.sheets.filter((sheet): sheet is string => typeof sheet === "string"),
						alreadyLoaded,
					});
					break;
				}
				case "sheets-available": {
					if (
						fileIndex === null ||
						typeof data.uploadId !== "string" ||
						typeof data.filename !== "string" ||
						!Array.isArray(data.availableSheets) ||
						!Array.isArray(data.alreadyLoadedSheets)
					) {
						return;
					}

					const availableNames = data.availableSheets.flatMap((s) =>
						isRecord(s) && typeof s.name === "string" ? [s.name] : [],
					);
					const alreadyLoadedNames = data.alreadyLoadedSheets.flatMap((s) =>
						isRecord(s) && typeof s.name === "string" ? [s.name] : [],
					);

					setPendingSheetSelect({
						uploadId: data.uploadId,
						fileIndex,
						filename: data.filename,
						sheets: availableNames,
						alreadyLoaded: alreadyLoadedNames,
					});
					break;
				}
				case "sheet-done": {
					if (fileIndex === null) return;
					const sheetIndex = toNumber(data.sheetIndex);
					const sheetName = typeof data.sheetName === "string" ? data.sheetName : null;
					if (sheetIndex === null || sheetName === null) return;
					upsertSheetStatus(fileIndex, {
						sheetIndex,
						sheetName,
						status: "done",
						fileId: toNumber(data.fileId) ?? undefined,
						fileType: typeof data.fileType === "string" ? data.fileType : undefined,
					});
					// Parent file status: "done" пока ни один лист не упал — переопределится "error" при sheet-error
					updateFileStatus(fileIndex, { status: "done" });
					break;
				}
				case "sheet-error": {
					if (fileIndex === null) return;
					const sheetIndex = toNumber(data.sheetIndex);
					const sheetName = typeof data.sheetName === "string" ? data.sheetName : null;
					if (sheetIndex === null || sheetName === null) return;
					upsertSheetStatus(fileIndex, {
						sheetIndex,
						sheetName,
						status: "error",
						error: typeof data.reason === "string" ? data.reason : undefined,
					});
					updateFileStatus(fileIndex, {
						status: "error",
						error: typeof data.reason === "string" ? data.reason : undefined,
					});
					break;
				}
				case "progress-update": {
					const total = toNumber(data.total);
					const completed = toNumber(data.completed);
					if (total !== null && completed !== null) {
						setProgress(total, completed);
					}
					break;
				}
				case "rate-book-exists": {
					if (
						fileIndex === null ||
						typeof data.uploadId !== "string" ||
						typeof data.filename !== "string" ||
						!isRecord(data.existingFile)
					) {
						return;
					}

					const ef = data.existingFile;
					setPendingConflict({
						uploadId: data.uploadId,
						fileIndex,
						filename: data.filename,
						conflictType: "rate-book",
						existingFile: {
							id: typeof ef.id === "number" ? ef.id : 0,
							originalName: typeof ef.originalName === "string" ? ef.originalName : "",
							fileType: typeof ef.fileType === "string" ? ef.fileType : "",
							createdAt: typeof ef.createdAt === "string" ? ef.createdAt : "",
						},
					});
					break;
				}
			}
		},
		[
			updateFileStatus,
			setPendingSheetSelect,
			setPendingConflict,
			addParsingResult,
			setProgress,
			upsertSheetStatus,
		],
	);

	const handleComplete = useCallback(() => {
		completeUpload();
		queryClient.invalidateQueries({ queryKey: ["files"] });
		setUploadId(null);
	}, [completeUpload, queryClient]);

	const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const fileList = e.target.files;
		if (!fileList || fileList.length === 0) return;

		const files = Array.from(fileList);
		setSelectedFiles(files);

		// Reset input to allow re-selecting same files
		if (inputRef.current) {
			inputRef.current.value = "";
		}

		try {
			const { uploadId: newUploadId } = await uploadFiles(files);
			startUpload(newUploadId);
			setUploadId(newUploadId);
		} catch (err) {
			const message = err instanceof Error ? err.message : "Не удалось отправить файлы на сервер";
			toast.error(message);
			failUpload(message);
			setUploadId(null);
		}
	};

	return (
		<>
			{uploadId && (
				<UploadSubscriber
					key={uploadId}
					uploadId={uploadId}
					onEvent={handleEvent}
					onComplete={handleComplete}
				/>
			)}
			<input
				ref={inputRef}
				type="file"
				accept=".xls,.xlsx"
				multiple
				className="hidden"
				onChange={handleChange}
			/>
			<Button
				type="button"
				onClick={handleClick}
				disabled={isUploading}
				className="bg-blue-600 text-white hover:bg-blue-700"
			>
				<Upload className="h-4 w-4" />
				{isUploading ? "Загрузка..." : "Загрузить файлы"}
			</Button>

			{pendingSheetSelect && (
				<SheetSelectDialog
					filename={pendingSheetSelect.filename}
					sheets={pendingSheetSelect.sheets}
					alreadyLoaded={pendingSheetSelect.alreadyLoaded}
					onSelect={handleSheetSelect}
					onCancel={handleSheetCancel}
				/>
			)}

			{pendingConflict && (
				<ConflictDialog
					conflict={pendingConflict}
					onReplace={handleConflictReplace}
					onCancel={handleConflictCancel}
				/>
			)}
		</>
	);
}
